import React, { useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { Icon, Button } from '@cbrebuild/blocks';
import SearchInput from '../../nucleus/search/search-input';
import LoadingBar from '../../nucleus/loading-bar/loading-bar';
import EmptyState from '../../components/empty-state/empty-state';
import AdminRestoreDealDetails from './admin-restore-deal-details';
import userEventService from '../../services/user-event-service';
import { searchDealsRestoreManagement, fetchDealAudit, restoreDeletedDeal } from '../../services/admin-service';

const AdminDealRestoreSearch = () => {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Local audit expansion state
  const [auditExpandedId, setAuditExpandedId] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTrailMap, setAuditTrailMap] = useState({}); // id -> trail array

  // Deal details expansion state
  const [expandedDealId, setExpandedDealId] = useState(null);
  const [dealDetailsLoading, setDealDetailsLoading] = useState(false);

  // Restoring state for tracking operations
  const [restoring, setRestoring] = useState({});

  const toggleExpand = async (deal, evt) => {
    if (evt) evt.stopPropagation();
    const idKey = deal.deal_iq_id || deal.id || deal.deal_pk || deal.pk; // display identifier

    // If clicking the same deal, collapse it
    if (expandedDealId === idKey) {
      setExpandedDealId(null);
      setAuditExpandedId(null);
      return;
    }

    // Expand the deal details
    setExpandedDealId(idKey);
    setDealDetailsLoading(false);
  };

  const handleLoadAudit = async (deal) => {
    const idKey = deal.deal_iq_id || deal.id || deal.deal_pk || deal.pk;
    const pkForAudit = deal.deal_pk || deal.pk || deal.id || deal.deal_iq_id;
    setAuditLoading(true);

    // Track audit view
    userEventService.trackEvent({
      eventCategory: 'Admin Capabilities',
      eventAction: 'admin_deal_audit_viewed',
      eventLabel: 'admin_manage_deal',
    });

    try {
      const trail = await fetchDealAudit(pkForAudit);
      setAuditTrailMap(prev => ({ ...prev, [idKey]: Array.isArray(trail) ? trail : [] }));
    } catch (e) {
      console.error('Failed to load audit trail for deal');
      setAuditTrailMap(prev => ({ ...prev, [idKey]: [] }));
    } finally {
      setAuditLoading(false);
    }
  };

  const searchDeals = useCallback(debounce(async (search, page = 1) => {
    if (!search || search.trim().length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      setTotalResults(0);
      setHasMore(false);
      return;
    }

    setIsSearching(true);

    // Track restore search
    if (page === 1) {
      userEventService.trackEvent({
        eventCategory: 'Admin Capabilities',
        eventAction: 'admin_deal_restore_search',
        eventLabel: 'admin_manage_deal',
      });
    }

    try {
      const response = await searchDealsRestoreManagement({
        search: search.trim(),
        page_size: 25,
        page: page,
      });

      const results = response.results || [];
      setSearchResults(prev => page === 1 ? results : [...prev, ...results]);
      setTotalResults(response.count || 0);
      setHasMore(Boolean(response.next));
      setCurrentPage(page);

      // Auto-expand if single result on first page
      if (page === 1 && results.length === 1) {
        toggleExpand(results[0]);
      }

    } catch (error) {
      setSearchResults([]);
      setTotalResults(0);
      setHasMore(false);
    } finally {
      setIsSearching(false);
    }
  }, 300), []);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
    searchDeals(value, 1);
  };

  const handleRestore = async (deal) => {
    const dealId = deal.deal_iq_id || deal.id;
    if (!dealId) return;
    setRestoring(prev => ({ ...prev, [dealId]: true }));

    // Track deal restore action
    userEventService.trackEvent({
      eventCategory: 'Admin Capabilities',
      eventAction: 'admin_deal_restore',
      eventLabel: 'admin_manage_deal',
    });
    
    try {
      const restored = await restoreDeletedDeal(dealId);
      // Refetch search results to get the latest status
      await searchDeals(searchTerm, 1);
    } catch (e) {
      console.error('Restore failed');
    } finally {
      setRestoring(prev => ({ ...prev, [dealId]: false }));
    }
  };


  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div className="search-loading">
          <LoadingBar />
          <p>Searching deals...</p>
        </div>
      );
    }

    if (!searchResults || searchResults.length === 0) {
      return null;
    }

    // For single result, show details directly
    if (searchResults.length === 1) {
      const deal = searchResults[0];
      return (
        <div className="deal-details-wrapper" style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '16px' }}>
          {dealDetailsLoading ? (
            <div className="deal-details-loading">
              <LoadingBar />
              <p>Loading deal details...</p>
            </div>
          ) : (
            <AdminRestoreDealDetails
              deal={deal}
              expandedDealId={expandedDealId}
              formatDate={(date) => date ? new Date(date).toLocaleDateString() : '--'}
              auditTrailMap={auditTrailMap}
              auditLoading={auditLoading}
              onRestore={handleRestore}
              onLoadAudit={handleLoadAudit}
            />
          )}
        </div>
      );
    }

    // For multiple results, show nothing
    return null;
  };

  return (
    <div className="admin-deal-restore-search">
      <div className="header-card">
        <div className="input-label">Search for Deals</div>
        <div className="search-deals">
          <SearchInput
            searchKey=""
            searchKeyAction="Enter deal ID or MTA Deal ID or Voucher Unique ID"
            handleSearchTermChange={handleSearchChange}
          />
          <div title="Enter Deal ID, MTA Deal ID, or Voucher Unique ID">
            <Icon iconName="info-circle" />
          </div>
        </div>
      </div>
      {!isSearching && searchResults.length === 0 && (
        <div className="empty-state-container">
          <EmptyState type="deals" title="Search for a deal or transaction to get started" />
        </div>
      )}
      {renderSearchResults()}
    </div>
  );
};

export default AdminDealRestoreSearch;

